import { useMutation, useQuery } from '@apollo/client';
import {
  Checkbox,
  Input,
  LabelButton,
  ModalContainer,
  MultiSelectPicker,
  MultiSelectPickerContent,
  MultiSelectPickerItem,
  MultiSelectPickerList,
  MultiSelectPickerTrigger,
  Select,
} from '@teamsnap/snap-ui';
import { Column } from 'components/common/table/paginated-table';
import { reactiveFeedbackMessage } from 'core/apollo-cache';
import environment from 'core/environment';
import CreateTemplateMutation from 'core/gql/mutations/create-export-template';

import { FeatureFlagConfig, FeatureFlags } from 'core/feature-flags';
import DeleteTemplateMutation from 'core/gql/mutations/delete-export-template';
import TemplatesQuery, {
  ExportTemplate,
} from 'core/gql/queries/export-templates';
import { FeatureFlagService } from 'frontend-toolkit';
import { AMPLITUDE_REGISTRATION_EXPORT_TYPE } from 'hooks/amplitude/types';
import { useAmplitude } from 'hooks/index';
import * as React from 'react';
import './ExportModal.scss';

type Props = {
  columns: Column[];
  title: string;
  show: boolean;
  onClose: () => any;
  onSubmit: (columns: Column[], exportType: string) => any;
  organizationId: number;
};

type ExportType =
  | 'form_results'
  | 'photos'
  | 'documents'
  | 'waivers'
  | 'line_item_details'
  | 'line_item_summary';

type ExportOption = { label: string; value: ExportType };

const exportTypes = (
  title: string,
  canSeeFeeSummary?: boolean
): ExportOption[] => {
  const defaultExportTypes: ExportOption[] = [
    {
      label: 'Form Data',
      value: 'form_results',
    },
  ];
  if (title === 'Generate Export') {
    defaultExportTypes.splice(1, 0, { label: 'Waivers', value: 'waivers' });
    defaultExportTypes.push({
      label: 'Fee Details',
      value: 'line_item_details',
    });
    if (canSeeFeeSummary)
      defaultExportTypes.push({
        label: 'Fee Summary',
        value: 'line_item_summary',
      });
  }

  return defaultExportTypes;
};

const ExportModal = ({
  title,
  show,
  onClose,
  columns,
  onSubmit,
  organizationId,
}: Props) => {
  const [exportType, setExportType] =
    React.useState<ExportType>('form_results');
  const [selectedColumns, setSelectedColumns] = React.useState<string[]>([]);
  const [selectedTemplate, setSelectedTemplate] = React.useState('manual');
  const [saveTemplate, setSaveTemplate] = React.useState(false);
  const [templateName, setTemplateName] = React.useState('');
  const [templateSubText, setTemplateSubText] = React.useState(undefined);

  const { amplitudeTrack } = useAmplitude();

  const [createTemplate] = useMutation(CreateTemplateMutation);
  const [deleteTemplate] = useMutation(DeleteTemplateMutation);

  const { data, refetch } = useQuery<{
    exportTemplates: ExportTemplate[];
  }>(TemplatesQuery, {
    variables: { organizationId },
    fetchPolicy: 'cache-first',
  });

  const templates = data?.exportTemplates || [];

  const disableGenerateButton =
    exportType === 'form_results'
      ? !selectedColumns.length || (saveTemplate && !templateName.trim())
      : false;

  const resetExportModal = () => {
    setSelectedTemplate('manual');
    setSelectedColumns([]);
    setTemplateName('');
    setSaveTemplate(false);
  };

  const onChangeTemplate = (templateId) => {
    setSelectedTemplate(templateId);
    if (templateId !== 'manual') {
      setTemplateSubText(undefined);
      const templateColumns = templates.find(
        (template) => template.id === templateId
      ).columns;
      setTemplateName(
        templates.find((template) => template.id === templateId).name
      );
      const selectedColumns = templateColumns.filter((templateColumn) =>
        columns.some((column) => column.name === templateColumn)
      );
      setSelectedColumns(selectedColumns);
    }
  };

  const onDeleteTemplate = () => {
    deleteTemplate({
      variables: {
        organizationId,
        id: +selectedTemplate,
      },
    }).then(() => {
      refetch({ organizationId });
      resetExportModal();
      reactiveFeedbackMessage({
        message: 'Template deleted',
        autoclose: true,
        isError: false,
      });
    });
  };

  const handleAmplitudeExport = (exportType: ExportType) => {
    let amplitudeExportType: AMPLITUDE_REGISTRATION_EXPORT_TYPE;
    switch (exportType) {
      case 'form_results':
        amplitudeExportType =
          AMPLITUDE_REGISTRATION_EXPORT_TYPE.REGISTRATION_DATA;
        break;
      case 'waivers':
        amplitudeExportType = AMPLITUDE_REGISTRATION_EXPORT_TYPE.WAIVERS;
        break;
      case 'line_item_details':
        amplitudeExportType = AMPLITUDE_REGISTRATION_EXPORT_TYPE.FEE_DETAILS;
        break;
      case 'line_item_summary':
        amplitudeExportType = AMPLITUDE_REGISTRATION_EXPORT_TYPE.FEE_SUMMARY;
        break;
      default:
        throw new Error(
          'Error: Unsupported Amplitude export type in ExportModal.'
        );
    }
    amplitudeTrack.tsbRegistrationExported({
      registration_export_type: amplitudeExportType,
    });
  };

  const onGenerateExport = () => {
    const cleanTemplateName = templateName.trim();
    if (saveTemplate && selectedTemplate === 'manual') {
      if (templates.some((x) => x.name === cleanTemplateName)) {
        setTemplateSubText('This template name already exists');
        return;
      }

      createTemplate({
        variables: {
          organizationId,
          name: cleanTemplateName,
          exportType: 'reg2_form_participant_export',
          columns: selectedColumns,
        },
      }).then(() => {
        refetch({ organizationId });
      });
    }
    resetExportModal();
    onSubmit(
      columns.filter((x) => selectedColumns.includes(x.name)),
      exportType
    );
    handleAmplitudeExport(exportType);
  };

  const handleOnClose = () => {
    resetExportModal();
    onClose();
  };

  const canSeeFeeSummary = FeatureFlagService.isFeatureEnabledForOrg(
    FeatureFlagConfig[FeatureFlags.FEE_SUMMARY_EXPORT],
    environment,
    organizationId
  );

  return (
    <ModalContainer closeButton={true} isOpen={show} onClose={handleOnClose}>
      <h2 data-testid="modal-heading" className="sui-mb-4">
        {title}
      </h2>

      <div className="sui-grid sui-gap-2">
        <Select
          name="exportType"
          label="Export Type"
          options={exportTypes(title, canSeeFeeSummary)}
          value={exportType}
          onChange={(value) => setExportType(value as ExportType)}
        />
        {exportType === 'form_results' && (
          <>
            <Select
              name="template"
              label="Template"
              options={[
                { label: 'Manually Select Columns', value: 'manual' },
                ...templates.map((template) => ({
                  label: template.name,
                  value: template.id,
                })),
              ]}
              value={selectedTemplate}
              onChange={(value) => onChangeTemplate(value)}
            />
            <MultiSelectPicker
              values={selectedColumns}
              name="Columns"
              label="Columns to Include"
              onValuesChange={(values) => {
                setSelectedTemplate('manual');
                setSelectedColumns(values);
              }}
            >
              <MultiSelectPickerTrigger
                placeholder="Columns"
                className="sui-w-full"
                data-testid="columns-selector"
              />
              <MultiSelectPickerContent
                className="sui-z-[99999]"
                data-testid="columns-selector-content"
              >
                <MultiSelectPickerList>
                  {columns.map((option) => (
                    <MultiSelectPickerItem
                      key={`MultiSelectPicker-option-column-${option.label}-${option.name}`}
                      value={option.name}
                      label={String(option.label)}
                    />
                  ))}
                </MultiSelectPickerList>
              </MultiSelectPickerContent>
            </MultiSelectPicker>
            <Checkbox
              id="saveTemplate"
              name="saveTemplate"
              label="Save this selection as a template"
              checked={saveTemplate}
              onChange={(e) => setSaveTemplate(e.target.checked)}
              data-testid="save-template-checkbox"
            />

            {saveTemplate && (
              <Input
                label="Template Name"
                name="templateName"
                helpText={templateSubText}
                onChange={(e) => {
                  setTemplateSubText(undefined);
                  setTemplateName(e.target.value);
                }}
                value={templateName}
                type="text"
              />
            )}
            {selectedTemplate !== 'manual' && (
              <LabelButton
                labelText="Delete template"
                icon="delete"
                variantType="tertiary"
                size="default"
                sentiment="negative"
                iconPosition="left"
                onClick={onDeleteTemplate}
              />
            )}
          </>
        )}

        <div className="sui-py-1 sui-flex sui-justify-end">
          <LabelButton
            labelText="Cancel"
            variantType="secondary"
            onClick={handleOnClose}
            size="small"
            className="sui-mr-1"
          />
          <LabelButton
            labelText="Generate Export"
            variantType="primary"
            onClick={onGenerateExport}
            size="small"
            data-testid="generate-export-button"
            disabled={disableGenerateButton}
          />
        </div>
      </div>
    </ModalContainer>
  );
};

export default ExportModal;
